<template>
  <div class="divisions-page">
    <section class="divisions-toolbar">
      <div class="divisions-toolbar__top">
        <div>
          <p class="divisions-toolbar__eyebrow">Площадки</p>
          <p class="divisions-toolbar__summary">
            {{ divisions.length }} всего
            <span class="divisions-toolbar__separator">·</span>
            {{ enabledReportingCount }} с отчётностью
          </p>
        </div>
        <CButton color="primary" class="divisions-toolbar__create" @click="openCreate">
          <span class="mdi mdi-plus"></span>
          Новая площадка
        </CButton>
      </div>

      <div class="divisions-toolbar__controls">
        <CInputGroup class="divisions-toolbar__search">
          <span class="input-group-text divisions-toolbar__search-icon">
            <span class="mdi mdi-magnify"></span>
          </span>
          <CFormInput
            v-model.trim="search"
            type="search"
            autocomplete="off"
            placeholder="Найти площадку"
          />
        </CInputGroup>
        <button
          class="divisions-toolbar__refresh"
          type="button"
          :disabled="loading"
          title="Обновить список"
          aria-label="Обновить список"
          @click="loadDivisions"
        >
          <span class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></span>
        </button>
      </div>
    </section>

    <div v-if="loading" class="divisions-state">
      <span class="divisions-loader"></span>
      <span>Загружаем площадки</span>
    </div>

    <div v-else-if="error" class="divisions-state divisions-state_error">
      <span class="mdi mdi-cloud-alert divisions-state__icon"></span>
      <span>{{ error }}</span>
      <CButton color="secondary" size="sm" @click="loadDivisions">
        Повторить
      </CButton>
    </div>

    <div v-else-if="filteredDivisions.length" class="divisions-grid">
      <article
        v-for="division in filteredDivisions"
        :key="division.guid"
        class="division-card"
      >
        <div class="division-card__header">
          <div class="division-card__identity">
            <div class="division-card__mark">
              {{ (division.name || '?').charAt(0).toUpperCase() }}
            </div>
            <div class="division-card__heading">
              <h2>{{ division.name || 'Без названия' }}</h2>
              <p>{{ division.address || 'Адрес не указан' }}</p>
            </div>
          </div>
          <span
            class="reporting-badge"
            :class="division.enableReporting ? 'reporting-badge_enabled' : 'reporting-badge_disabled'"
            :title="division.enableReporting ? 'Отчётность включена' : 'Отчётность отключена'"
          >
            <span class="reporting-badge__dot"></span>
            {{ division.enableReporting ? 'Отчётность' : 'Выключена' }}
          </span>
        </div>

        <div class="division-card__details">
          <div class="division-card__detail">
            <span class="mdi mdi-tag-outline"></span>
            <span class="division-card__detail-label">Версия</span>
            <strong>{{ division.version || '—' }}</strong>
          </div>
          <div class="division-card__detail">
            <span class="mdi mdi-folder-outline"></span>
            <span class="division-card__detail-label">Группа</span>
            <strong class="division-card__uuid" :title="division.divisionGroupId">
              {{ division.divisionGroupId || '—' }}
            </strong>
          </div>
        </div>

        <div class="division-card__footer">
          <span class="division-card__guid" :title="division.guid">{{ division.guid }}</span>
          <div class="division-card__actions">
            <button
              class="division-card__action"
              type="button"
              :disabled="pingingGuid === division.guid"
              title="Принудительная проверка"
              aria-label="Принудительная проверка площадки"
              @click="pingDivision(division)"
            >
              <span
                class="mdi"
                :class="pingingGuid === division.guid ? 'mdi-loading mdi-spin' : 'mdi-access-point'"
              ></span>
            </button>
            <button
              class="division-card__action"
              type="button"
              title="Редактировать"
              aria-label="Редактировать площадку"
              @click="openEdit(division)"
            >
              <span class="mdi mdi-pencil-outline"></span>
            </button>
            <button
              class="division-card__action division-card__action_danger"
              type="button"
              :disabled="deletingGuid === division.guid"
              title="Удалить"
              aria-label="Удалить площадку"
              @click="deleteDivision(division)"
            >
              <span
                class="mdi"
                :class="deletingGuid === division.guid ? 'mdi-loading mdi-spin' : 'mdi-trash-can-outline'"
              ></span>
            </button>
          </div>
        </div>
      </article>
    </div>

    <div v-else class="divisions-state">
      <span class="mdi mdi-office-building-outline divisions-state__icon"></span>
      <strong>{{ search ? 'Ничего не нашли' : 'Площадок пока нет' }}</strong>
      <span>{{ search ? 'Попробуйте изменить запрос' : 'Создайте первую площадку, чтобы начать работу' }}</span>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'
import { CButton, CFormInput, CInputGroup } from '@coreui/bootstrap-vue'
import DivisionRecordEdit from '@/components/editor/DivisionRecordEdit.vue'
import { ApiSys } from '@/services/api'
import { notifyPingResult, unwrapPingStatus } from '@/services/division-ping'
import { useDefaultStore } from '@/store'

export default {
  name: 'DivisionsPage',
  components: {
    CButton,
    CFormInput,
    CInputGroup
  },
  setup() {
    const store = useDefaultStore()
    const { showModal, hideModal } = inject('modal')
    return {
      store,
      showModal,
      hideModal
    }
  },
  data() {
    return {
      divisions: [],
      loading: true,
      error: '',
      search: '',
      deletingGuid: null,
      pingingGuid: null
    }
  },
  computed: {
    enabledReportingCount() {
      return this.divisions.filter(division => division.enableReporting).length
    },
    filteredDivisions() {
      const search = this.search.toLowerCase()
      if (!search) {
        return this.divisions
      }

      return this.divisions.filter(division => [
        division.name,
        division.address,
        division.version,
        division.divisionGroupId,
        division.guid
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(search))
    }
  },
  async created() {
    this.store.setPageTitle('Управление площадками')
    await this.loadDivisions()
  },
  methods: {
    errorMessage(response, fallback) {
      return response?.error?.response?.data?.message ||
        response?.error?.response?.data?.error?.message ||
        response?.error?.message ||
        fallback
    },
    normalizeDivisions(response) {
      if (response?.error) {
        throw new Error(this.errorMessage(response, 'Не удалось загрузить площадки'))
      }

      const divisions = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : []

      return divisions
        .map(division => ({
          ...division,
          enableReporting: division.enableReporting !== false
        }))
        .sort((left, right) => (left.name || '').localeCompare(
          right.name || '',
          'ru',
          { numeric: true, sensitivity: 'base' }
        ))
    },
    async loadDivisions() {
      this.loading = true
      this.error = ''
      const response = await ApiSys.getAdminDivisionRecords()

      try {
        this.divisions = this.normalizeDivisions(response)
      } catch (error) {
        this.divisions = []
        this.error = error.message
        this.$toast.error(this.error)
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.showModal(
        DivisionRecordEdit,
        { hide: this.hideModal },
        { saved: this.loadDivisions },
        'Создать площадку',
        'lg'
      )
    },
    openEdit(division) {
      this.showModal(
        DivisionRecordEdit,
        { division, hide: this.hideModal },
        { saved: this.loadDivisions },
        `Редактировать: ${division.name}`,
        'lg'
      )
    },
    async pingDivision(division) {
      if (!division?.guid || this.pingingGuid) {
        return
      }

      this.pingingGuid = division.guid
      const response = await ApiSys.pingAdminDivision(division.guid)
      this.pingingGuid = null

      if (response?.error) {
        this.$toast.error(this.errorMessage(response, 'Не удалось проверить площадку'))
        return
      }

      notifyPingResult(this.$toast, unwrapPingStatus(response), division.name || division.guid)
    },
    async deleteDivision(division) {
      if (!window.confirm(`Точно удалить площадку "${division.name}"?`)) {
        return
      }

      this.deletingGuid = division.guid
      const response = await ApiSys.deleteAdminDivisionRecord(division.guid)
      this.deletingGuid = null

      if (response?.error) {
        this.$toast.error(this.errorMessage(response, 'Не удалось удалить площадку'))
        return
      }

      this.$toast.success('Площадка удалена')
      await this.loadDivisions()
    }
  }
}
</script>

<style scoped>
.divisions-page {
  height: 100%;
  padding: 1.5rem clamp(1rem, 3vw, 2.5rem) 2.5rem;
  overflow-y: auto;
}

.divisions-toolbar {
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.5rem;
  background:
    radial-gradient(circle at 92% -30%, rgba(56, 154, 240, 0.18), transparent 45%),
    rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

.divisions-toolbar__top,
.divisions-toolbar__controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.divisions-toolbar__top {
  margin-bottom: 1rem;
}

.divisions-toolbar__eyebrow {
  margin: 0 0 0.15rem;
  color: #f2f5f8;
  font-size: 1rem;
  font-weight: 650;
}

.divisions-toolbar__summary {
  margin: 0;
  color: #7f8a97;
  font-size: 0.76rem;
}

.divisions-toolbar__separator {
  margin: 0 0.25rem;
  color: #4d5966;
}

.divisions-toolbar__create {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.divisions-toolbar__search {
  width: min(100%, 480px);
}

.divisions-toolbar__search-icon {
  color: #788491;
  background: rgba(0, 0, 0, 0.12);
  border-color: rgba(255, 255, 255, 0.12);
}

.divisions-toolbar__refresh {
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  color: #8793a0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9px;
  cursor: pointer;
  transition: 0.15s ease;
}

.divisions-toolbar__refresh:hover:not(:disabled) {
  color: #d9e2eb;
  background: rgba(255, 255, 255, 0.06);
}

.divisions-toolbar__refresh:disabled {
  opacity: 0.55;
  cursor: default;
}

.divisions-state {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
  color: #8d98a5;
  text-align: center;
}

.divisions-state strong {
  color: #d1d8df;
  font-size: 1rem;
}

.divisions-state__icon {
  color: #53606d;
  font-size: 2.2rem;
}

.divisions-state_error {
  color: #ff8b8b;
}

.divisions-loader {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: divisions-spin 0.65s linear infinite;
}

.divisions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  gap: 0.85rem;
}

.division-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 1rem;
  overflow: hidden;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.018));
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 13px;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.division-card::before {
  position: absolute;
  top: 0;
  left: 1rem;
  width: 42px;
  height: 2px;
  background: #389af0;
  border-radius: 0 0 3px 3px;
  content: '';
}

.division-card:hover {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.025));
  border-color: rgba(56, 154, 240, 0.24);
  transform: translateY(-1px);
}

.division-card__header,
.division-card__identity,
.division-card__footer,
.division-card__actions {
  display: flex;
  align-items: center;
}

.division-card__header {
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.division-card__identity {
  min-width: 0;
  gap: 0.7rem;
}

.division-card__mark {
  display: grid;
  place-items: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  color: #a8d2ff;
  background: rgba(56, 154, 240, 0.13);
  border: 1px solid rgba(56, 154, 240, 0.2);
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
}

.division-card__heading {
  min-width: 0;
}

.division-card__heading h2 {
  margin: 0;
  overflow: hidden;
  color: #f0f3f6;
  font-size: 0.92rem;
  font-weight: 650;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.division-card__heading p {
  margin: 0.12rem 0 0;
  overflow: hidden;
  color: #717d89;
  font-size: 0.73rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.division-card__details {
  display: grid;
  gap: 0.45rem;
  padding: 0.85rem 0 0.8rem;
  margin-top: 0.8rem;
  border-top: 1px solid rgba(255, 255, 255, 0.055);
}

.division-card__detail {
  display: grid;
  grid-template-columns: 18px 54px minmax(0, 1fr);
  align-items: center;
  gap: 0.35rem;
  color: #66727f;
  font-size: 0.74rem;
}

.division-card__detail-label {
  color: #66727f;
}

.division-card__detail strong {
  min-width: 0;
  overflow: hidden;
  color: #aeb8c2;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.division-card__uuid,
.division-card__guid {
  font-family: ui-monospace, 'Cascadia Code', monospace;
}

.division-card__footer {
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: auto;
}

.division-card__guid {
  min-width: 0;
  overflow: hidden;
  color: #4e5965;
  font-size: 0.62rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.division-card__actions {
  flex-shrink: 0;
  gap: 0.25rem;
}

.division-card__action {
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  color: #7d8995;
  background: transparent;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.15s ease;
}

.division-card__action:hover:not(:disabled) {
  color: #b8d9fa;
  background: rgba(56, 154, 240, 0.12);
}

.division-card__action_danger:hover:not(:disabled) {
  color: #ff9696;
  background: rgba(255, 85, 85, 0.1);
}

.division-card__action:disabled {
  opacity: 0.45;
  cursor: default;
}

.reporting-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.35rem;
  padding: 0.25rem 0.52rem;
  border-radius: 999px;
  font-size: 0.64rem;
  font-weight: 550;
}

.reporting-badge__dot {
  width: 5px;
  height: 5px;
  background: currentColor;
  border-radius: 50%;
}

.reporting-badge_enabled {
  color: #62d89b;
  background: rgba(45, 180, 112, 0.14);
}

.reporting-badge_disabled {
  color: #aeb7c2;
  background: rgba(255, 255, 255, 0.08);
}

@keyframes divisions-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 767px) {
  .divisions-page {
    padding: 1rem;
  }

  .divisions-toolbar__top {
    align-items: stretch;
    flex-direction: column;
  }

  .divisions-toolbar__create {
    justify-content: center;
  }
}
</style>
